import { createContext, useContext, ReactNode, useState } from 'react';

import { OperationVariables, useMutation, useQuery } from '@apollo/client';

import { useAuth } from './Auth';

import {
  CHECKOUT_START,
  CHECKOUT_COMPLETE,
  SET_SUBSCRIPTION_CHOSEN,
  LIST_MEMBERSHIP_PLANS,
} from '../http/apollo-queries';

import { IMembership, IMembershipPlan } from '../types/model';
import {
  Chargebee,
  ChargebeeInstance,
  CheckoutCompleteResponse,
  CheckoutStartResponse,
} from '../pages/Subscriptions/types';
import { useSession } from './Session';
import { logger } from '../helpers/logger';

interface IMembershipContext {
  membership: IMembership;
  membershipPlans: IMembershipPlan[];
  membershipPlansLoading: boolean;
  checkoutLoading: boolean;
  inCheckout: boolean;
  initChargebee: () => ChargebeeInstance;
  handleChargebeeCheckout: (
    email: string,
    membershipPlanId: string,
    priceSku: string,
    onComplete: () => any,
    onError: () => any
  ) => void;

  checkoutStart(
    variables?: Partial<OperationVariables> | undefined
  ): Promise<any>;

  checkoutComplete(
    variables?: Partial<OperationVariables> | undefined
  ): Promise<any>;

  setSubscriptionChosen(
    variables?: Partial<OperationVariables> | undefined
  ): Promise<any>;
}

interface Props {
  children?: ReactNode;
}

const actions = {
  checkoutStart: (): any => {},
  checkoutComplete: (): any => {},
  setSubscriptionChosen: (): any => {},
  initChargebee: (): any => {},
  handleChargebeeCheckout: (): any => {},
};

const MembershipContext = createContext<IMembershipContext>({
  membership: {} as IMembership,
  membershipPlans: [],
  checkoutLoading: false,
  membershipPlansLoading: false,
  inCheckout: false,
  ...actions,
});

export const useMembership = () => useContext(MembershipContext);

export const MembershipProvider = ({ children }: Props) => {
  const { user, refetch: refetchUser } = useAuth();
  const session = useSession();

  const [inCheckout, setInCheckout] = useState(false);

  const {
    data: { listMembershipPlans: { items: membershipPlans = [] } = {} } = {},
    loading: membershipPlansLoading,
  } = useQuery<{ listMembershipPlans: { items: IMembershipPlan[] } }>(
    LIST_MEMBERSHIP_PLANS,
    {
      nextFetchPolicy: 'cache-only',
      fetchPolicy: 'cache-and-network',
      skip: !user?.membership?.id,
    }
  );

  const [checkoutStart, { loading: checkoutStartLoading }] =
    useMutation<CheckoutStartResponse>(CHECKOUT_START);

  const [checkoutComplete, { loading: checkoutCompleteLoading }] =
    useMutation<CheckoutCompleteResponse>(CHECKOUT_COMPLETE);

  const [setSubscriptionChosen] = useMutation(SET_SUBSCRIPTION_CHOSEN);

  logger.log('🟡 MembershipContext:MembershipPlans', membershipPlans);

  let cbInstance;

  const initChargebee = () => {
    const chargebee = window['Chargebee'] as Chargebee;
    cbInstance = chargebee.init({
      site: process.env.REACT_APP_CHARGEBEE_SITE,
      publishableKey: process.env.REACT_APP_CHARGEBEE_KEY,
    });
    return cbInstance;
  };

  const handleChargebeeCheckout = (
    email,
    membershipPlanId,
    priceSku,
    onComplete,
    onError
  ) => {
    setInCheckout(true);
    cbInstance.openCheckout({
      hostedPage: () => {
        session.removeSessionItem('checkoutCompleted');
        session.removeSessionItem('cbId');
        session.removeSessionItem('checkoutToken');

        return new Promise((resolve, reject) => {
          checkoutStart({
            variables: {
              input: {
                email,
                priceSku,
              },
            },
          })
            .then(({ data }) => {
              session.setSessionItem(
                'checkoutToken',
                data?.checkoutStart?.token || ''
              );
              return resolve(data?.checkoutStart);
            })
            .catch((err) => {
              return reject(err);
            });
        });
      },
      success(hostPageId) {
        logger.log('Checkout:hostedPage:success', hostPageId);

        checkoutComplete({
          variables: {
            input: {
              hostPageId,
              membershipId: user!.membership.id,
              membershipPlanId,
              priceSku,
              token: session.getSessionItem('checkoutToken'),
            },
          },
        })
          .then((res) => {
            session.setSessionItem(
              'cbId',
              res.data?.checkoutComplete?.membership?.cbId || ''
            );
            return refetchUser();
          })
          .then(() => {
            session.setSessionItem('checkoutCompleted', 'true');
          })
          .catch((err) => logger.error(err));
      },
      close: () => {
        if (session.getSessionItem('checkoutCompleted') === 'true') {
          onComplete();
        } else {
          session.removeSessionItem('checkoutCompleted');
          session.removeSessionItem('cbId');
          session.removeSessionItem('checkoutToken');
        }
        setInCheckout(false);
      },
      error: () => {
        setInCheckout(false);
        onError();
      },
    });
  };

  const membershipPlan = membershipPlans.find(
    ({ id }) => id === user?.membership.membershipPlanId
  )!;

  const value = {
    membership: { ...user?.membership!, membershipPlan },
    membershipPlans,
    checkoutLoading: checkoutStartLoading || checkoutCompleteLoading,
    membershipPlansLoading,
    ...actions,
    checkoutStart,
    checkoutComplete,
    setSubscriptionChosen,
    initChargebee,
    handleChargebeeCheckout,
    inCheckout,
  };

  return (
    <MembershipContext.Provider value={value}>
      {children}
    </MembershipContext.Provider>
  );
};
